import { useEffect, useState, useCallback } from 'react'
import { apiSetPieceStats } from '../server/ServiceCall'
import { AuthState } from '../store/auth'

interface ClubRow {
  CLUB_NAME: string
  CORNERS: number; FREEKICKS: number; PENALTIES: number; OFFSIDES: number; MATCHES: number
}
interface PlayerRow {
  PLAYER_NAME: string; CLUB_NAME: string
  OFFSIDES: number; PENALTIES: number; CORNERS: number; FREEKICKS: number; TOTAL: number
}

interface Props { auth: AuthState; leagueCode: number; seasonNo: number; nonce?: number }

export default function SetPiece({ auth, leagueCode, seasonNo, nonce }: Props) {
  const [clubs, setClubs]     = useState<ClubRow[]>([])
  const [players, setPlayers] = useState<PlayerRow[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr]         = useState('')

  const reload = useCallback(async () => {
    setLoading(true); setErr('')
    const r = await apiSetPieceStats(auth.token, leagueCode, seasonNo)
    if (r.resultCode === '00') {
      try {
        const o = JSON.parse(r.resultData) as { clubs: ClubRow[], players: PlayerRow[] }
        setClubs(o.clubs ?? []); setPlayers(o.players ?? [])
      } catch { setErr('데이터 파싱 오류') }
    } else setErr(r.resultMessage || '조회 실패')
    setLoading(false)
  }, [auth.token, leagueCode, seasonNo])

  useEffect(() => { reload() }, [reload, nonce])

  if (loading) return <div className="page-pad">세트피스 통계 불러오는 중…</div>

  const tot = clubs.reduce((a, c) => ({
    CORNERS: a.CORNERS + c.CORNERS, FREEKICKS: a.FREEKICKS + c.FREEKICKS,
    PENALTIES: a.PENALTIES + c.PENALTIES, OFFSIDES: a.OFFSIDES + c.OFFSIDES,
  }), { CORNERS: 0, FREEKICKS: 0, PENALTIES: 0, OFFSIDES: 0 })
  const hasData = clubs.length > 0

  return (
    <div className="page-pad setpiece-page">
      <h2>세트피스 통계 — 시즌 {seasonNo}</h2>
      {err && <p className="msg">{err}</p>}

      {!hasData && !err && (
        <section className="card setpiece-empty">
          <p>아직 세트피스 기록이 없습니다.</p>
          <p className="muted">경기(라이브·일괄)를 진행하면 코너·프리킥·페널티·오프사이드가 누적되어 여기에 집계됩니다.</p>
        </section>
      )}

      {hasData && (
        <>
          <section className="card setpiece-totals">
            <div><label>코너</label><strong>{tot.CORNERS.toLocaleString()}</strong></div>
            <div><label>프리킥</label><strong>{tot.FREEKICKS.toLocaleString()}</strong></div>
            <div><label>페널티</label><strong>{tot.PENALTIES.toLocaleString()}</strong></div>
            <div><label>오프사이드</label><strong>{tot.OFFSIDES.toLocaleString()}</strong></div>
          </section>

          <section className="card">
            <h3>구단별 세트피스</h3>
            <table className="data-table">
              <thead><tr><th>구단</th><th>코너</th><th>프리킥</th><th>페널티</th><th>오프사이드</th><th>경기</th></tr></thead>
              <tbody>
                {clubs.map(c => (
                  <tr key={c.CLUB_NAME}>
                    <td>{c.CLUB_NAME}</td>
                    <td className="num">{c.CORNERS}</td>
                    <td className="num">{c.FREEKICKS}</td>
                    <td className="num">{c.PENALTIES}</td>
                    <td className="num">{c.OFFSIDES}</td>
                    <td className="num muted">{c.MATCHES}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="card">
            <h3>세트피스 관여 선수 TOP {players.length}</h3>
            <table className="data-table">
              <thead><tr><th>#</th><th>선수</th><th>구단</th><th>코너</th><th>프리킥</th><th>페널티</th><th>오프사이드</th><th>합계</th></tr></thead>
              <tbody>
                {players.map((p, i) => (
                  <tr key={p.PLAYER_NAME + p.CLUB_NAME}>
                    <td className="num muted">{i + 1}</td>
                    <td>{p.PLAYER_NAME}</td>
                    <td className="muted">{p.CLUB_NAME}</td>
                    <td className="num">{p.CORNERS}</td>
                    <td className="num">{p.FREEKICKS}</td>
                    <td className="num">{p.PENALTIES}</td>
                    <td className="num">{p.OFFSIDES}</td>
                    <td className="num"><strong>{p.TOTAL}</strong></td>
                  </tr>
                ))}
                {players.length === 0 && <tr><td colSpan={8} className="muted">선수 기록 없음</td></tr>}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  )
}
